/**
 * /referrals — authed page where power users get + share their link.
 * =====================================================================
 * Two states:
 *   1. **Locked** (verified savings < $10K):  shows progress bar +
 *      explanation + link to Engineer Savings Wall to log more wins.
 *   2. **Unlocked** (≥ $10K):  full referral link, copy/X/LinkedIn
 *      share buttons, conversion stats, my-referral list, public
 *      leaderboard preview.
 *
 * Brand: Slate-950 base, emerald accents, soft amber for warnings.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Sparkles, Copy, Check, Link as LinkIcon, Share2, Twitter, Linkedin,
  Trophy, ArrowRight, Loader2, Lock, TrendingUp, Users,
} from 'lucide-react';
import { toast } from 'sonner';
import BrandTitle from '../components/BrandTitle';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtUsd = (n) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const Referrals = () => {
  const [link, setLink] = useState(null);
  const [conversions, setConversions] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const auth = useCallback(() => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b, c] = await Promise.all([
        axios.get(`${API_URL}/referrals/my-link`, { headers: auth() }),
        axios.get(`${API_URL}/referrals/my-conversions`, { headers: auth() }),
        axios.get(`${API_URL}/referrals/leaderboard`),
      ]);
      setLink(a.data);
      setConversions(b.data?.referrals || []);
      setLeaderboard(c.data?.leaderboard || []);
    } catch (e) {
      toast.error('Could not load referrals — please refresh.');
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  const handleCopy = async () => {
    if (!link?.share_url) return;
    try {
      await navigator.clipboard.writeText(link.share_url);
      setCopied(true);
      toast.success('Link copied');
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error('Copy failed — please select manually'); }
  };

  const tweetIntent = link
    ? `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        `Saved real money on cloud + AI bills with @cartie_ai. If you ship FinOps, this is the missing piece. ${link.share_url}`,
      )}`
    : '#';

  const linkedinIntent = link
    ? `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link.share_url)}`
    : '#';

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-2 text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading your referrals…
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-5xl mx-auto" data-testid="referrals-page">
      <header>
        <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-400/30 mb-3">
          <Sparkles className="w-3.5 h-3.5 text-emerald-300" />
          <span className="text-[11px] font-semibold tracking-widest text-emerald-200 uppercase">
            Power-user referrals
          </span>
        </div>
        <h1 className="text-3xl font-bold text-white tracking-tight"><BrandTitle>Recommend a friend</BrandTitle></h1>
        <p className="text-slate-400 text-sm mt-1.5 max-w-3xl">
          Engineers who've shipped real savings on CARTIE unlock a personal share link.
          When a friend you invite crosses <strong>$5K saved</strong>, both of you get
          a public credit on the leaderboard.
        </p>
      </header>

      {/* Locked state */}
      {!link?.unlocked && (
        <Card className="bg-slate-900/60 border-amber-500/30" data-testid="referrals-locked">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-amber-400" />
              <CardTitle className="text-base text-white">
                Unlock your referral link at $10K verified savings
              </CardTitle>
            </div>
            <CardDescription className="text-slate-400">
              Why high-bar? Referrals carry weight when the referrer has actually shipped wins.
              You're at <strong className="text-emerald-300">{fmtUsd(link?.progress_usd)}</strong> of{' '}
              <strong>$10,000</strong>.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Progress value={link?.progress_pct || 0} data-testid="referrals-progress-bar" />
            <p className="text-xs text-slate-400">
              Log the next win on the{' '}
              <Link to="/engineer-savings-wall" className="text-emerald-300 hover:text-emerald-200">
                Engineer Savings Wall
              </Link>
              {' '}— admin verifies, then it counts toward unlock.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Unlocked — share + stats */}
      {link?.unlocked && (
        <>
          <Card className="bg-gradient-to-br from-emerald-500/10 to-cyan-500/5 border-emerald-500/30" data-testid="referrals-unlocked">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-emerald-300" />
                <CardTitle className="text-base text-white">Your personal share link is live</CardTitle>
                <Badge variant="outline" className="border-emerald-400/40 text-emerald-300 text-[11px]">
                  Power user
                </Badge>
              </div>
              <CardDescription className="text-slate-400">
                Anyone visiting this link sees your verified savings as social proof.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-2">
                <code className="flex-1 bg-slate-950 border border-emerald-500/20 rounded-md p-3 text-emerald-100 text-sm font-mono break-all" data-testid="referrals-share-url">
                  {link.share_url}
                </code>
                <Button
                  onClick={handleCopy}
                  className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold whitespace-nowrap"
                  data-testid="referrals-copy-btn"
                >
                  {copied ? <><Check className="w-4 h-4 mr-1.5" /> Copied</> : <><Copy className="w-4 h-4 mr-1.5" /> Copy</>}
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                <a href={tweetIntent} target="_blank" rel="noopener noreferrer">
                  <Button size="sm" variant="outline" className="border-emerald-400/40 text-cyan-200 hover:bg-emerald-500/10" data-testid="referrals-share-twitter">
                    <Twitter className="w-3.5 h-3.5 mr-1.5" /> Share on X
                  </Button>
                </a>
                <a href={linkedinIntent} target="_blank" rel="noopener noreferrer">
                  <Button size="sm" variant="outline" className="border-emerald-400/40 text-cyan-200 hover:bg-emerald-500/10" data-testid="referrals-share-linkedin">
                    <Linkedin className="w-3.5 h-3.5 mr-1.5" /> Share on LinkedIn
                  </Button>
                </a>
                <Link to={link.share_url.replace(/^https?:\/\/[^/]+/, '')}>
                  <Button size="sm" variant="ghost" className="text-slate-400 hover:text-white" data-testid="referrals-preview-btn">
                    <Share2 className="w-3.5 h-3.5 mr-1.5" /> Preview the page
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Stats */}
          <div className="grid sm:grid-cols-4 gap-3" data-testid="referrals-stats">
            {[
              { k: 'total_clicks',     l: 'Clicks',     icon: LinkIcon, color: 'cyan-300' },
              { k: 'total_signups',    l: 'Signups',    icon: Users,    color: 'emerald-300' },
              { k: 'total_converted',  l: 'Converted',  icon: Sparkles, color: 'emerald-300' },
            ].map((s) => {
              const Icon = s.icon;
              return (
                <Card key={s.k} className="bg-slate-900/60 border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className={`w-3.5 h-3.5 text-${s.color}`} />
                      <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">{s.l}</span>
                    </div>
                    <p className="text-2xl font-bold text-white" data-testid={`stat-${s.k}`}>
                      {(link.stats?.[s.k] ?? 0).toLocaleString()}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
            <Card className="bg-slate-900/60 border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-3.5 h-3.5 text-emerald-300" />
                  <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">Downstream saved</span>
                </div>
                <p className="text-2xl font-bold text-emerald-300" data-testid="stat-downstream">
                  {fmtUsd(link.stats?.total_downstream_savings_usd)}
                </p>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* My referrals list */}
      {link?.unlocked && (
        <Card className="bg-slate-900/60 border-white/10">
          <CardHeader>
            <CardTitle className="text-base text-white">Your referrals</CardTitle>
          </CardHeader>
          <CardContent>
            {conversions.length === 0 ? (
              <p className="text-slate-400 text-sm py-6 text-center" data-testid="referrals-empty-list">
                No referrals yet. Share your link with FinOps-savvy friends — engineers who already log savings convert at ~3×.
              </p>
            ) : (
              <ul className="divide-y divide-white/5" data-testid="referrals-list">
                {conversions.map((r) => (
                  <li key={r.id} className="py-2.5 flex items-center justify-between gap-3 text-sm" data-testid={`referral-row-${r.id}`}>
                    <div className="min-w-0">
                      <p className="text-slate-100 truncate">{r.referee_email || '—'}</p>
                      <p className="text-xs text-slate-400">
                        {r.signed_up_at ? `Signed up ${new Date(r.signed_up_at).toLocaleDateString()}` : 'Not signed up yet'}
                      </p>
                    </div>
                    {r.converted_at ? (
                      <Badge className="bg-emerald-500/15 text-emerald-200 border-emerald-500/30">
                        <Check className="w-3 h-3 mr-1" />
                        Converted · {fmtUsd(r.converted_savings_usd)}
                      </Badge>
                    ) : r.signed_up_at ? (
                      <Badge variant="outline" className="border-emerald-400/30 text-cyan-200">Pending</Badge>
                    ) : (
                      <Badge variant="outline" className="border-slate-500/30 text-slate-400">Visited</Badge>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      {/* Public leaderboard — visible to everyone */}
      <Card className="bg-slate-900/60 border-white/10" data-testid="referrals-leaderboard">
        <CardHeader>
          <CardTitle className="text-base text-white flex items-center gap-2">
            <Trophy className="w-4 h-4 text-amber-300" /> Leaderboard
          </CardTitle>
          <CardDescription className="text-slate-400">
            Power users with the most downstream savings unlocked.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {leaderboard.length === 0 ? (
            <p className="text-slate-400 text-sm py-6 text-center">
              No converted referrals yet. Be the first.
            </p>
          ) : (
            <ol className="divide-y divide-white/5">
              {leaderboard.map((row, idx) => (
                <li key={row.code} className="py-2.5 flex items-center justify-between gap-3" data-testid={`leaderboard-row-${idx}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-slate-400 font-mono text-xs w-6">#{idx + 1}</span>
                    <span className="text-slate-100 truncate">{row.name}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-slate-400 text-xs">
                      {row.total_converted} {row.total_converted === 1 ? 'conversion' : 'conversions'}
                    </span>
                    <Badge className="bg-emerald-500/15 text-emerald-200 border-emerald-500/30">
                      {fmtUsd(row.total_downstream_savings_usd)} unlocked
                    </Badge>
                  </div>
                </li>
              ))}
            </ol>
          )}
          <div className="mt-4 text-center">
            <Link to="/engineer-savings-wall" className="text-sm text-emerald-300 hover:text-emerald-200 inline-flex items-center gap-1.5">
              See the Engineer Savings Wall <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Referrals;
